import * as THREE from "three";
import type { WaterPlacement } from "./SceneEnvironment.helpers";

/** Peak shoreline recede (m) — capped to a third of a shallow lake's depth. */
export function shoreInsetAmp(depth: number): number {
	return Math.min(2.2, depth * 0.35);
}

/**
 * Deterministic, smooth, organic shoreline recede in `[0, insetAmp]` as a
 * function of world/local x (the meshes are centred at x=0). Same shape
 * every reload — no `Math.random`. Shared by the water near edge, the sand
 * band and the shore rocks so they all meet the same waterline.
 */
export function shorelineRecede(x: number, insetAmp: number): number {
	const n =
		0.5 +
		0.3 * Math.sin(x * 0.55 + 1.3) +
		0.14 * Math.sin(x * 1.21 + 4.7) +
		0.06 * Math.sin(x * 2.7 + 0.6);
	return Math.max(0, Math.min(1, n)) * insetAmp;
}

/** World Z of the waterline at world x for a placed lake. */
export function waterlineZAt(x: number, placement: WaterPlacement): number {
	const depth = placement.size[1];
	return (
		placement.center[2] -
		depth / 2 +
		shorelineRecede(x - placement.center[0], shoreInsetAmp(depth))
	);
}

/** Segments along the shore — enough to resolve the wavy waterline smoothly. */
const SHORE_SEGMENTS_X = 256;
const SHORE_SEGMENTS_Z = 24;
/** Rows across the sand band (it is thin, so a few suffice). */
const SAND_ROWS = 6;
/** How far the sand reaches from the waterline onto the land (m). */
const SAND_INLAND_M = 1.25;

/**
 * Flat (XZ, y=0) water geometry whose NEAR edge meanders like a shoreline.
 * Centred at the local origin; the caller positions it at the placement
 * centre. Carries a per-vertex `aShore` attribute (distance from the waterline
 * in metres) for the shallow-water tint.
 */
export function createShoreWaterGeometry(
	width: number,
	depth: number,
): THREE.BufferGeometry {
	const halfW = width / 2;
	const halfD = depth / 2;
	const insetAmp = shoreInsetAmp(depth);
	const cols = SHORE_SEGMENTS_X + 1;
	const positions: number[] = [];
	const normals: number[] = [];
	const uvs: number[] = [];
	const shore: number[] = [];

	for (let iz = 0; iz <= SHORE_SEGMENTS_Z; iz += 1) {
		const tz = (iz / SHORE_SEGMENTS_Z) ** 2; // Concentrate rows near shore.
		for (let ix = 0; ix <= SHORE_SEGMENTS_X; ix += 1) {
			const tx = ix / SHORE_SEGMENTS_X;
			const x = -halfW + tx * width;
			const nearZ = -halfD + shorelineRecede(x, insetAmp);
			const z = nearZ + (halfD - nearZ) * tz;
			positions.push(x, 0, z);
			normals.push(0, 1, 0);
			uvs.push(tx, tz);
			shore.push(z - nearZ);
		}
	}

	const indices: number[] = [];
	for (let iz = 0; iz < SHORE_SEGMENTS_Z; iz += 1) {
		for (let ix = 0; ix < SHORE_SEGMENTS_X; ix += 1) {
			const a = iz * cols + ix;
			const b = a + 1;
			const c = a + cols;
			const d = c + 1;
			indices.push(a, c, b, b, c, d);
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.setAttribute("aShore", new THREE.Float32BufferAttribute(shore, 1));
	geometry.setIndex(indices);
	geometry.computeBoundingSphere();
	return geometry;
}

/**
 * A thin sand band hugging the SAME wavy waterline as the water (so they meet
 * seamlessly), reaching `SAND_INLAND_M` onto the land. Carries a per-vertex
 * `aSand` attribute (1 at the waterline → 0 at the inland edge) used to fade
 * the sand into the grass and to darken it toward wet sand at the water.
 */
export function createShoreSandGeometry(
	width: number,
	depth: number,
): THREE.BufferGeometry {
	const halfW = width / 2;
	const halfD = depth / 2;
	const insetAmp = shoreInsetAmp(depth);
	const cols = SHORE_SEGMENTS_X + 1;
	const positions: number[] = [];
	const normals: number[] = [];
	const uvs: number[] = [];
	const sand: number[] = [];

	for (let iz = 0; iz <= SAND_ROWS; iz += 1) {
		const tz = iz / SAND_ROWS; // 0 = inland edge, 1 = waterline
		for (let ix = 0; ix <= SHORE_SEGMENTS_X; ix += 1) {
			const tx = ix / SHORE_SEGMENTS_X;
			const x = -halfW + tx * width;
			const waterEdge = -halfD + shorelineRecede(x, insetAmp);
			const bandWidth = SAND_INLAND_M * (0.75 + 0.25 * Math.sin(x * 0.73 + 2));
			const inlandEdge = waterEdge - bandWidth;
			const z = inlandEdge + (waterEdge - inlandEdge) * tz;
			positions.push(x, 0, z);
			normals.push(0, 1, 0);
			uvs.push(tx, tz);
			sand.push(tz);
		}
	}

	const indices: number[] = [];
	for (let iz = 0; iz < SAND_ROWS; iz += 1) {
		for (let ix = 0; ix < SHORE_SEGMENTS_X; ix += 1) {
			const a = iz * cols + ix;
			const b = a + 1;
			const c = a + cols;
			const d = c + 1;
			indices.push(a, c, b, b, c, d);
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.setAttribute("aSand", new THREE.Float32BufferAttribute(sand, 1));
	geometry.setIndex(indices);
	geometry.computeBoundingSphere();
	return geometry;
}
